/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useEffect, useState } from "react";
import { View } from "react-native";
import { useLazyQuery } from "@apollo/client";

import { useDispatch, useSelector } from "react-redux";
import { ActionCreators } from "actions";
import { RootState } from "store/types";
import {
  GET_USER_STORE_LIST,
  GET_USER_DISTRICT_LIST,
  GET_USER_REGION_LIST,
  GET_SKILL_LIST,
} from "graphql/internal/Queries";

import { styles } from "UpduoLibrary";
import { sortByKey } from "config/Utilities";
import Breadcrumb from "./Breadcrumb";
import ExploreFilter from "./ExploreFilter";
import { DashboardExploreObject } from "components/DashboardRoute";

interface ExploreBarProps {
  exploreObject: DashboardExploreObject;
  category: string;
  onChangeFilterRegion: (id: string) => void;
  onChangeFilterDistrict: (id: string) => void;
  onChangeFilterStore: (id: string) => void;
}

export default function ExploreBar({
  exploreObject,
  category,
  onChangeFilterRegion,
  onChangeFilterDistrict,
  onChangeFilterStore,
}: ExploreBarProps) {
  const dispatch = useDispatch();
  const authToken = useSelector((state: RootState) => state.authToken);
  const filterRegionId = useSelector(
    (state: RootState) => state.filterRegionId
  );
  const filterDistrictId = useSelector(
    (state: RootState) => state.filterDistrictId
  );
  const filterStoreId = useSelector((state: RootState) => state.filterStoreId);
  const regionList = useSelector((state: RootState) => state.regionList);
  const districtList = useSelector((state: RootState) => state.districtList);
  const storeList = useSelector((state: RootState) => state.storeList);

  const [isLoading, setIsLoading] = useState(true);

  const [getRegionList, {}] = useLazyQuery(GET_USER_REGION_LIST, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      let newRegionList =
        (data?.regions?.["edges"]?.map((region) => region["node"]) as any[]) ||
        ([] as any[]);
      if (newRegionList.length > 0) {
        newRegionList = sortByKey(newRegionList, "name").filter(
          (item) => item.name !== "Maintenance"
        );
      }
      dispatch(ActionCreators.setRegionList(newRegionList));
    },
  });

  const [getDistrictList, {}] = useLazyQuery(GET_USER_DISTRICT_LIST, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      let newDistrictList =
        (data?.districts?.["edges"]?.map(
          (district) => district["node"]
        ) as any[]) || ([] as any[]);
      if (newDistrictList.length > 0) {
        newDistrictList = sortByKey(newDistrictList, "name").filter(
          (item) => item.name !== "Maintenance"
        );
      }
      dispatch(ActionCreators.setDistrictList(newDistrictList));
    },
  });

  const [getStoreList, {}] = useLazyQuery(GET_USER_STORE_LIST, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      let newStoreList =
        (data?.stores?.["edges"]?.map((store) => store["node"]) as any[]) ||
        ([] as any[]);
      if (newStoreList.length > 0) {
        newStoreList = sortByKey(newStoreList, "name").filter(
          (item) => item.name !== "Maintenance"
        );
      }
      dispatch(ActionCreators.setStoreList(newStoreList));
    },
  });

  const [getSkillList, {}] = useLazyQuery(GET_SKILL_LIST, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const newSkillList =
        data?.skills?.["edges"]?.map((skill) => skill["node"]) || [];
      dispatch(ActionCreators.setSkillList(newSkillList));
    },
  });

  useEffect(() => {
    Promise.all([
      getDistrictList(),
      getRegionList(),
      getStoreList(),
      getSkillList(),
    ]).then(() => {
      setIsLoading(false);
    });
  }, [authToken]);

  if (isLoading) {
    return null;
  }

  return (
    <View style={styles.subNavigationBar}>
      <Breadcrumb
        category={category}
        exploreObject={exploreObject}
        url={"dashboard"}
      />
      <View
        style={{
          flexDirection: "row",
          marginTop: -26,
          marginRight: -30,
          height: 30,
        }}
      >
        <ExploreFilter
          value={filterRegionId}
          onChange={onChangeFilterRegion}
          primaryText={"Regions"}
          optionList={regionList}
        />
        <ExploreFilter
          value={filterDistrictId}
          onChange={onChangeFilterDistrict}
          primaryText={"Districts"}
          optionList={districtList}
        />
        <ExploreFilter
          value={filterStoreId}
          onChange={onChangeFilterStore}
          primaryText={"Stores"}
          optionList={storeList}
        />
      </View>
    </View>
  );
}
